import { useState, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { MessageSquare, MessageCircle } from "lucide-react"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import { format } from "date-fns"
import { InfoTooltip } from "../shared/InfoTooltip"
import { ExportDialog, ExportButton, type ExportColumn } from "../shared/ExportDialog"
import { CHART_CONFIG } from "../../utils/constants"
import { formatDuration } from "../../utils/formatting"
import type { RawContact, RawMessage } from "../types"
import type { OverviewAnalytics, DailyTrend, HourlyData, DashboardCommentItem } from "../../hooks/useAnalytics"

interface RatingDistribution {
  thumbsUp: number
  thumbsDown: number
  total: number
}

interface CorrectedResponse {
  messageId: string
  contactId: string
  content: string
  correctedResponse: string
  timestamp: number
  contact?: RawContact
}

interface MessagesTabProps {
  filteredMessages: RawMessage[]
  overviewAnalytics: OverviewAnalytics
  dailyTrends: DailyTrend[]
  hourlyDistribution: HourlyData[]
  ratingDistribution: RatingDistribution
  correctedResponsesList: CorrectedResponse[]
  commentsList: DashboardCommentItem[]
  onOpenInHITL?: (contact: RawContact) => void
}

export function MessagesTab({
  filteredMessages,
  overviewAnalytics,
  dailyTrends,
  hourlyDistribution,
  ratingDistribution,
  correctedResponsesList,
  commentsList,
  onOpenInHITL,
}: MessagesTabProps) {
  const [exportOpen, setExportOpen] = useState(false)
  const [exportCommentsOpen, setExportCommentsOpen] = useState(false)

  const { exportColumns, exportData } = useMemo(() => {
    const varKeys = new Set<string>()
    filteredMessages.forEach(m => {
      if (m.vars) Object.keys(m.vars).forEach(k => varKeys.add(k))
    })

    const columns: ExportColumn[] = [
      { key: 'id', label: 'Message ID' },
      { key: 'contact_id', label: 'Contact ID' },
      { key: 'direction', label: 'Direction' },
      { key: 'content', label: 'Content' },
      { key: 'time_created', label: 'Time Created' },
      { key: 'channel', label: 'Channel' },
      { key: 'service_name', label: 'Service', defaultSelected: false },
      ...Array.from(varKeys).sort().map(k => ({
        key: `var_${k}`,
        label: `var: ${k}`,
        defaultSelected: ['rating', 'high_risk_flag', 'low_confidence_flag', 'asked_human_flag', 'sent_from', 'risk_category_name'].includes(k),
      })),
    ]

    const data = filteredMessages.map(m => {
      const row: Record<string, any> = {
        id: m.id,
        contact_id: m.contact_id,
        direction: m.direction,
        content: m.content,
        time_created: format(new Date(m.time_created * 1000), 'yyyy-MM-dd HH:mm:ss'),
        channel: m.channel || '',
        service_name: m.service_name || '',
      }
      if (m.vars) {
        for (const [k, v] of Object.entries(m.vars)) {
          row[`var_${k}`] = v != null ? String(v) : ''
        }
      }
      return row
    })

    return { exportColumns: columns, exportData: data }
  }, [filteredMessages])

  const exportCommentsData = useMemo(() => {
    const rows: Record<string, any>[] = []

    commentsList.forEach(c => {
      rows.push({
        Type: 'Comment',
        Subtype: c.type === 'message' ? 'Message' : 'Conversation',
        Contact_ID: c.contactId,
        Contact_Name: c.contact?.name ?? '',
        Message_ID: c.messageId ?? '',
        Associated_Message: c.associatedMessage ?? '',
        Comment: c.commentText,
        Correction: '',
        Commenter: c.commenterName,
        Timestamp: format(new Date(c.timestamp * 1000), 'yyyy-MM-dd HH:mm:ss'),
      })
    })
    correctedResponsesList.forEach(item => {
      rows.push({
        Type: 'Corrected Response',
        Subtype: '',
        Contact_ID: item.contactId,
        Contact_Name: item.contact?.name ?? '',
        Message_ID: item.messageId,
        Associated_Message: '',
        Comment: item.content ?? '',
        Correction: item.correctedResponse ?? '',
        Commenter: '',
        Timestamp: format(new Date(item.timestamp * 1000), 'yyyy-MM-dd HH:mm:ss'),
      })
    })
    return rows
  }, [commentsList, correctedResponsesList])

  const exportCommentsColumns = useMemo((): ExportColumn[] => [
    { key: 'Type', label: 'Type' },
    { key: 'Subtype', label: 'Subtype' },
    { key: 'Contact_ID', label: 'Contact ID' },
    { key: 'Contact_Name', label: 'Contact Name' },
    { key: 'Message_ID', label: 'Message ID' },
    { key: 'Associated_Message', label: 'Associated Message' },
    { key: 'Comment', label: 'Comment / Bot said' },
    { key: 'Correction', label: 'Correction / Should have said' },
    { key: 'Commenter', label: 'Commenter' },
    { key: 'Timestamp', label: 'Timestamp' },
  ], [])

  return (
    <div className="space-y-6">
      <div className="flex justify-end gap-2">
        <ExportButton onClick={() => setExportOpen(true)} label="Export Messages" />
        <ExportButton onClick={() => setExportCommentsOpen(true)} label="Export Comments" />
      </div>

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        title="Export Messages"
        description={`Export ${filteredMessages.length} messages as CSV — choose which columns to include`}
        columns={exportColumns}
        data={exportData}
        filename="telerivet_messages"
      />

      <ExportDialog
        open={exportCommentsOpen}
        onOpenChange={setExportCommentsOpen}
        title="Export Comments"
        description={`Export ${exportCommentsData.length} rows (message & conversation comments + corrected responses) as CSV`}
        columns={exportCommentsColumns}
        data={exportCommentsData}
        filename="comments_and_corrected_responses"
      />

      {/* Message Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground uppercase">
              Total Messages
              <InfoTooltip text="All messages sent and received during the selected period" />
            </div>
            <div className="text-2xl font-bold whitespace-nowrap">{filteredMessages.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground uppercase">
              Incoming
              <InfoTooltip text="Messages received from users" />
            </div>
            <div className="text-2xl font-bold whitespace-nowrap text-blue-600">{overviewAnalytics.incomingMessages}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground uppercase">
              Outgoing
              <InfoTooltip text="Messages sent to users (bot + human responses)" />
            </div>
            <div className="text-2xl font-bold whitespace-nowrap text-green-600">{overviewAnalytics.outgoingMessages}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground uppercase">
              AI Responses
              <InfoTooltip text="Outgoing responses generated by the bot (no human operator)" />
            </div>
            <div className="text-2xl font-bold whitespace-nowrap text-green-600">{overviewAnalytics.aiResponses}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground uppercase">
              Human Responses
              <InfoTooltip text="Outgoing responses sent by a human operator (moderation / HITL)" />
            </div>
            <div className="text-2xl font-bold whitespace-nowrap text-teal-600">{overviewAnalytics.humanResponses}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground uppercase">
              Response Rate
              <InfoTooltip text="Percentage of incoming messages that received at least one reply" />
            </div>
            <div className="text-2xl font-bold whitespace-nowrap text-purple-600">{overviewAnalytics.responseRate}%</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground uppercase">
              Median Response Time
              <InfoTooltip text="Median time between receiving a user message and sending a reply (less affected by outliers)" />
            </div>
            <div className="text-2xl font-bold whitespace-nowrap text-orange-600">{formatDuration(overviewAnalytics.medianResponseTime)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Message Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Message Volume by Day
              <InfoTooltip text="Daily breakdown of incoming and outgoing messages" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={CHART_CONFIG} className="h-[300px] w-full">
              <BarChart data={dailyTrends}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" className="text-xs" />
                <YAxis className="text-xs" />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="incoming" stackId="a" fill="#3b82f6" name="Incoming" />
                <Bar dataKey="outgoing" stackId="a" fill="#10b981" name="Outgoing" />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Message Activity by Hour
              <InfoTooltip text="When users are most active. Helps identify peak hours for support." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={CHART_CONFIG} className="h-[300px] w-full">
              <BarChart data={hourlyDistribution}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="label" className="text-xs" interval={2} />
                <YAxis className="text-xs" />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="incoming" stackId="a" fill="#3b82f6" name="Incoming" />
                <Bar dataKey="outgoing" stackId="a" fill="#10b981" name="Outgoing" />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Feedback Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            User Feedback
            <InfoTooltip text="Thumbs up/down feedback on bot responses from users" />
          </CardTitle>
          <CardDescription>
            {ratingDistribution.total} rated responses • {overviewAnalytics.correctedResponses} with corrections
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">👍</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Thumbs Up</span>
                    <span className="text-sm text-muted-foreground">{ratingDistribution.thumbsUp}</span>
                  </div>
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 rounded-full transition-all"
                      style={{ width: `${ratingDistribution.total > 0 ? (ratingDistribution.thumbsUp / ratingDistribution.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-2xl">👎</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Thumbs Down</span>
                    <span className="text-sm text-muted-foreground">{ratingDistribution.thumbsDown}</span>
                  </div>
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-red-500 rounded-full transition-all"
                      style={{ width: `${ratingDistribution.total > 0 ? (ratingDistribution.thumbsDown / ratingDistribution.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-center space-y-2">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-4xl font-bold text-green-600">
                  {ratingDistribution.total > 0 ? Math.round((ratingDistribution.thumbsUp / ratingDistribution.total) * 100) : 0}%
                </div>
                <div className="text-sm text-muted-foreground">Positive Feedback Rate</div>
              </div>
              {overviewAnalytics.correctedResponses > 0 && (
                <div className="text-center text-sm text-muted-foreground">
                  {overviewAnalytics.correctedResponses} responses have suggested corrections
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Corrected Responses */}
      {correctedResponsesList.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Corrected Responses
              <Badge variant="secondary">{correctedResponsesList.length}</Badge>
              <InfoTooltip text="Bot responses that received thumbs down with a suggested correction. Click contact to open in Moderation." />
            </CardTitle>
            <CardDescription>
              Messages where users provided feedback on what the response should have said
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              <div className="space-y-4">
                {correctedResponsesList.slice(0, 50).map((item) => (
                  <div key={item.messageId} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={onOpenInHITL && item.contact ? "cursor-pointer hover:bg-blue-100 hover:border-blue-400" : ""}
                          onClick={() => { if (item.contact) onOpenInHITL?.(item.contact) }}
                          title={onOpenInHITL ? "Click to open in Moderation" : undefined}
                        >
                          {item.contact?.name || item.contact?.phone_number || 'Unknown Contact'}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(item.timestamp * 1000), 'MMM d, yyyy HH:mm')}
                        </span>
                      </div>
                      <span className="text-lg">👎</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <div className="text-xs font-medium text-red-600 uppercase">Bot Said:</div>
                        <div className="text-sm bg-red-50 p-2 rounded border border-red-100 line-clamp-3">
                          {item.content || '(No content)'}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-xs font-medium text-green-600 uppercase">Should Have Said:</div>
                        <div className="text-sm bg-green-50 p-2 rounded border border-green-100 line-clamp-3">
                          {item.correctedResponse}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Comments */}
      {commentsList.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              Comments
              <Badge variant="secondary">{commentsList.length}</Badge>
              <InfoTooltip text="Internal comments on messages and conversations. Click a row to open in Moderation." />
            </CardTitle>
            <CardDescription>
              Message-level and conversation-level comments from reviewers
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              <div className="space-y-3">
                {commentsList.slice(0, 100).map((item, idx) => (
                  <div
                    key={`${item.contactId}-${item.messageId ?? 'conv'}-${item.timestamp}-${idx}`}
                    className={`border rounded-lg p-3 ${onOpenInHITL ? 'cursor-pointer hover:bg-muted/60' : ''}`}
                    onClick={() => onOpenInHITL?.(item.contact)}
                    role={onOpenInHITL ? 'button' : undefined}
                    title={onOpenInHITL ? 'Open conversation in Moderation' : undefined}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <Badge variant="outline" className="shrink-0">
                        {item.contact?.name || item.contact?.phone_number || 'Unknown'}
                      </Badge>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {format(new Date(item.timestamp * 1000), 'MMM d, yyyy HH:mm')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary" className="text-[10px]">
                        {item.type === 'message' ? 'Message' : 'Conversation'}
                      </Badge>
                      {item.commenterName && (
                        <span className="text-xs text-muted-foreground">{item.commenterName}</span>
                      )}
                    </div>
                    <div className="text-sm mt-2 line-clamp-3">{item.commentText}</div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
